/**
 * OrderConfirmation Page
 * Success page after order placement
 * Displays order details and next steps
 */

import { useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useProviderConfig } from '../../contexts/ProviderConfigContext';

export function OrderConfirmation() {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useProviderConfig();

  // Get order number from location state
  const orderNumber = location.state?.orderNumber;

  // Redirect if accessed directly without order data
  useEffect(() => {
    if (!orderId || !orderNumber) {
      navigate('../');
    }
  }, [orderId, orderNumber, navigate]);

  if (!orderId || !orderNumber) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-xl font-semibold text-gray-900">
            {config?.companyName || 'Order Confirmation'}
          </h1>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
          {/* Success icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <svg
              className="h-10 w-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Success message */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
            Your order has been successfully placed and is being processed.
          </p>

          {/* Order number */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <p className="text-sm font-medium text-gray-600 mb-2">Order Number</p>
            <p className="text-2xl sm:text-3xl font-bold text-primary break-all">{orderNumber}</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-2">
              Please save this number for your records
            </p>
          </div>

          {/* Next steps */}
          <div className="text-left bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">What Happens Next?</h3>
            <ol className="space-y-3">
              <li className="flex items-start">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary-500 text-white text-sm font-semibold mr-3 flex-shrink-0">
                  1
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">Confirmation Email</p>
                  <p className="text-sm text-gray-600">
                    You will receive an email confirmation with order details shortly
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary-500 text-white text-sm font-semibold mr-3 flex-shrink-0">
                  2
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">Delivery Confirmation</p>
                  <p className="text-sm text-gray-600">
                    We will contact you within 24 hours to confirm your delivery date and time
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary-500 text-white text-sm font-semibold mr-3 flex-shrink-0">
                  3
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">Container Delivery</p>
                  <p className="text-sm text-gray-600">
                    Your container will be delivered on the scheduled date
                  </p>
                </div>
              </li>
            </ol>
          </div>

          {/* Account info (if no password was set) */}
          <div className="text-left bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex">
              <svg
                className="h-5 w-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-amber-800 mb-1">Account Access</h3>
                <p className="text-sm text-amber-700">
                  If you did not create a password during checkout, check your email for a link
                  to set up your account and track your order online.
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="../login"
              className="inline-flex items-center justify-center min-h-[48px] px-6 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              Sign In to View Order
            </Link>
            <Link
              to="../order"
              className="inline-flex items-center justify-center min-h-[48px] px-6 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              Place Another Order
            </Link>
          </div>

          {/* Contact information */}
          {config && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-3">Need help with your order?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm">
                <a
                  href={`tel:${config.contactPhone}`}
                  className="text-primary hover:text-primary-700 font-medium"
                >
                  {config.contactPhone}
                </a>
                <span className="hidden sm:inline text-gray-400">|</span>
                <a
                  href={`mailto:${config.contactEmail}`}
                  className="text-primary hover:text-primary-700 font-medium"
                >
                  {config.contactEmail}
                </a>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
